import React from 'react';
import DrillOver from '../../shared/DrillOver';
import { SkeletonText } from '../../shared/Skeleton';
import { useTileData } from '../../../hooks/useTileData';
import { getWorkList } from '../../../api/hub/work';
import { BUCKETS } from '../work/constants';
import { fmtNumber } from '../kit';
import RailRow from './RailRow';
import { BlockError } from './states';
import { itemMetaParts, workItemTint } from './WorkCard';

const BUCKET_LABEL = Object.fromEntries(BUCKETS.map((b) => [b.key, b.label]));
const labelOf = (key) => BUCKET_LABEL[key] || String(key || 'Other').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** The owner arrives as `owner` on the client context and may be `owner_name` on the work list. */
const asRowItem = (it) => ({ ...it, owner: it?.owner || it?.owner_name || null });

/**
 * "N open →": this client's open work, read from the same work list the Work page uses
 * (`fn_work_list` for one client, grouped by bucket), in the app's one slide-over. The groups
 * are the work list's own buckets, so the total here is that list's total.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.clientId
 */
export default function OpenWorkSheet({ open, onClose, clientId }) {
  // `open` is a dependency, not just the enabled flag: the hook only reads again when its dependencies change,
  // so opening the drawer is what triggers the read (and closing and reopening reads fresh work).
  const work = useTileData(() => getWorkList({ client_id: clientId, group_by: 'bucket', limit: 200 }), [clientId, open], open && !!clientId);
  if (!open) return null;

  const data = work.data?.data ?? null;
  const groups = (Array.isArray(data?.groups) ? data.groups : []).filter((g) => Array.isArray(g?.items) && g.items.length > 0);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  const pending = !work.data && !work.error;

  let body;
  if (work.loading || pending) {
    body = (
      <div role="status" aria-label="Loading">
        <SkeletonText lines={4} lineClass="h-10" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  } else if (work.error) {
    body = <BlockError error={work.error} what="this client's open work" message="Couldn't load this client's open work." onRetry={work.refresh} padding="p-0" />;
  } else if (groups.length === 0) {
    body = <p className="py-4 text-sm text-gray-500 dark:text-gray-400">No open work for this client.</p>;
  } else {
    body = (
      <div className="space-y-6" data-testid="open-work-sheet-groups">
        {groups.map((g) => (
          <section key={g.key} aria-label={labelOf(g.key)}>
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              {labelOf(g.key)} · {fmtNumber(g.items.length)}
            </h3>
            <div className="space-y-2.5">
              {g.items.map((raw, i) => {
                const it = asRowItem(raw);
                return (
                  <RailRow
                    key={it?.id || `${g.key}-${i}`} testId="open-work-row" rail={workItemTint(it)}
                    title={it?.title || 'Untitled work'} meta={itemMetaParts(it)}
                    hint={it?.stalled && it?.stalled_reason ? it.stalled_reason : undefined}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <DrillOver
      stack={[{
        label: 'Open work',
        subtitle: !work.error && !pending && !work.loading ? `${fmtNumber(total)} open ${total === 1 ? 'item' : 'items'}` : undefined,
        body,
      }]}
      onBack={onClose}
      onClose={onClose}
    />
  );
}
